import { SaveOutlined } from '@ant-design/icons';
import { DatePicker, Form, Input, Select } from 'antd';
import SubmitFormButton from 'common/components/submit-form-button.component';
import { GeneralTranscription, UserTranscription } from 'config/constants/transcription.constant';
import UserService from 'config/services/user.service';
import AuthIntentUtils from 'config/utils/auth-intent.util';
import { isAuthError } from 'config/utils/errors.util';
import { DATE_FORMAT } from 'config/utils/globals.util';
import { SnackbarUtilities } from 'config/utils/snackbar.util';
import UserUtils from 'config/utils/user.util';
import dayjs from 'dayjs';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/index';
import { AnyAction, Dispatch } from 'redux';
import { User } from 'store/models/user.model';
import { UserSlice } from 'store/slices/user.slice';

type ProfileFormProps = {
  userProfile: User;
  callBack?: (value: any) => void;
};

const ProfileForm: React.FC<ProfileFormProps> = ({ userProfile, callBack }) => {
  const [profileForm] = Form.useForm<User>();
  const countryDisplay = useSelector((state: RootState) => state.setting.countryDisplay);

  const formInitValues = {
    ...userProfile,
    birthDate: userProfile.birthDate ? dayjs(userProfile.birthDate, DATE_FORMAT) : '',
  };

  const dispatch: Dispatch<AnyAction> = useDispatch();

  const handleSubmit = async (userProfile: User): Promise<void> => {
    await AuthIntentUtils.withAuthRetry(async () => {
      dispatch(UserSlice.fetchingRefresh(true));
      try {
        await UserService.updateUserProfile(userProfile);
        const getuserProfileResult = await UserService.getUserProfile();
        dispatch(UserSlice.refreshUserProfile(getuserProfileResult));
        callBack?.(false);
      } catch (error) {
        if (isAuthError(error)) throw error;
        SnackbarUtilities.error(UserTranscription.ERROR_UPDATING_USER_PROFILE);
      } finally {
        dispatch(UserSlice.fetchingRefresh(false));
      }
    });
  };

  return (
    <Form
      form={profileForm}
      name='profile-form'
      layout='vertical'
      autoComplete='off'
      initialValues={formInitValues}
      onFinish={handleSubmit}
    >
      <Form.Item
        label={`${UserTranscription.USER_NAME} ${GeneralTranscription.MANDATORY}`}
        name='name'
        rules={UserUtils.userNameFieldRules}
        validateTrigger='onBlur'
      >
        <Input placeholder={UserTranscription.USER_NAME_PLACEHOLDER} />
      </Form.Item>
      <Form.Item
        label={`${UserTranscription.USER_LAST_NAME} ${GeneralTranscription.MANDATORY}`}
        name='lastName'
        rules={UserUtils.userLastNameFieldRules}
        validateTrigger='onBlur'
      >
        <Input placeholder={UserTranscription.USER_LAST_NAME_PLACEHOLDER} />
      </Form.Item>
      <Form.Item
        label={`${UserUtils.getDniCopy().label} ${GeneralTranscription.MANDATORY}`}
        name='dni'
        rules={UserUtils.getUserDniFieldRules()}
        validateTrigger='onBlur'
        getValueFromEvent={(event) => UserUtils.formatDni(event.target.value)}
      >
        <Input placeholder={UserUtils.getDniCopy().placeholder} />
      </Form.Item>
      <Form.Item
        label={`${UserTranscription.USER_EMAIL} ${GeneralTranscription.MANDATORY}`}
        name='email'
        rules={UserUtils.userEmailFieldRules}
        validateTrigger='onBlur'
      >
        <Input placeholder={UserTranscription.USER_EMAIL_PLACEHOLDER} disabled={Boolean(userProfile.email)} />
      </Form.Item>
      <Form.Item
        label={`${UserTranscription.USER_PHONE} ${GeneralTranscription.MANDATORY}`}
        name='phone'
        rules={UserUtils.userPhoneFieldRules}
        validateTrigger='onBlur'
      >
        <Input placeholder={UserTranscription.USER_PHONE_PLACEHOLDER} addonBefore={countryDisplay.phonePrefix} />
      </Form.Item>
      <Form.Item
        label={`${UserTranscription.USER_BIRTH_DATE} ${GeneralTranscription.MANDATORY}`}
        name='birthDate'
        rules={UserUtils.userBirhDateRules}
        validateTrigger='onBlur'
      >
        <DatePicker style={{ width: '100%' }} placeholder={UserTranscription.USER_BIRTH_DATE_PLACEHOLDER} />
      </Form.Item>
      <Form.Item label={UserTranscription.USER_GENRE} name='genre' rules={UserUtils.userGenreFieldRules} validateTrigger='onBlur'>
        <Select placeholder={UserTranscription.USER_GENRE_PLACEHOLDER} options={UserUtils.handleGenreOption()} />
      </Form.Item>
      <Form.Item>
        <SubmitFormButton
          form={profileForm}
          text={GeneralTranscription.SAVE}
          icon={<SaveOutlined />}
          skipLiveValidation={UserUtils.DNI_SKIP_LIVE_VALIDATION}
        />
      </Form.Item>
    </Form>
  );
};
export default ProfileForm;
